// WasmCompiler — a MunosCompiler backed by the Rust `munos-compiler.wasm`
// module. It instantiates the module once (async) and then exposes a
// SYNCHRONOUS `compile`, driving the ABI and decoding the result blob into the
// same CompileResult the TypeScript compiler returns (or throwing CompileError).
//
// This module is ISOMORPHIC: it takes the wasm bytes as input and does not
// touch the filesystem, so it is safe to bundle for the browser. Node hosts
// that read the wasm off disk use `loadWasmCompiler` in `./wasm-compiler-node`;
// the browser fetches the bundled asset and calls `instantiateWasmCompiler`
// directly.
//
// The ABI + result-blob layout it decodes is documented in
// docs/rust-compiler-migration.md.
import { CompileError } from "./compiler-contract.js";
const TARGET_CODE = { client: 0, server: 1 };
// ---- result-blob decoding (mirrors build_result_blob in lib.rs) ----
class BlobReader {
    buf;
    o = 0;
    dv;
    constructor(buf) {
        this.buf = buf;
        this.dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    }
    u8() { return this.buf[this.o++]; }
    u32() { const v = this.dv.getUint32(this.o, true); this.o += 4; return v; }
    i64() { const v = this.dv.getBigInt64(this.o, true); this.o += 8; return v; }
    bytes(n) { const b = this.buf.slice(this.o, this.o + n); this.o += n; return b; }
    str() { return new TextDecoder().decode(this.bytes(this.u32())); }
}
function decodeConstValue(r) {
    const kind = r.u8();
    switch (kind) {
        case 0: return { kind: "int", value: r.i64() };
        case 1: return { kind: "bool", value: r.u8() !== 0 };
        case 2: return { kind: "string", bytes: r.bytes(r.u32()) };
        case 3: {
            const count = r.u32();
            const elements = [];
            for (let i = 0; i < count; i++)
                elements.push(decodeConstValue(r));
            return { kind: "array", elements };
        }
        default: throw new Error(`WasmCompiler: unknown ConstValue kind ${kind}`);
    }
}
export function decodeResult(blob) {
    const r = new BlobReader(blob);
    const status = r.u8();
    if (status === 1)
        throw new CompileError(r.str());
    const wasm = r.bytes(r.u32());
    const heapStart = r.u32();
    const usesEscapedHeap = r.u8() !== 0;
    const readNames = () => {
        const n = r.u32();
        const out = new Set();
        for (let i = 0; i < n; i++)
            out.add(r.str());
        return out;
    };
    const rewindableEvents = readNames();
    const barrierRewindableEvents = readNames();
    const sharedConsts = [];
    const scCount = r.u32();
    for (let i = 0; i < scCount; i++) {
        const name = r.str();
        sharedConsts.push({ name, value: decodeConstValue(r) });
    }
    const diagnostics = [];
    const dCount = r.u32();
    for (let i = 0; i < dCount; i++) {
        const line = r.u32();
        const column = r.u32();
        const message = r.str();
        diagnostics.push({ message, line, column });
    }
    return {
        wasm: wasm,
        heapStart,
        usesEscapedHeap,
        rewindableEvents,
        barrierRewindableEvents,
        sharedConsts,
        diagnostics,
    };
}
// Wrap an instantiated compiler module as a synchronous MunosCompiler. The
// module's own `compile` is synchronous (it drives alloc/compile/result_len in
// one call), so both the async and sync instantiation paths share this.
function makeCompiler(instance) {
    const ex = instance.exports;
    const writeString = (s) => {
        const enc = new TextEncoder().encode(s);
        const ptr = ex.alloc(enc.length);
        // Re-read the buffer each time: alloc may have grown (and detached) memory.
        new Uint8Array(ex.memory.buffer, ptr, enc.length).set(enc);
        return { ptr, len: enc.length };
    };
    return {
        compile(source, target, system = "nes") {
            const src = writeString(source);
            const sys = writeString(system);
            const resPtr = ex.compile(src.ptr, src.len, TARGET_CODE[target], sys.ptr, sys.len);
            const resLen = ex.result_len();
            const blob = new Uint8Array(ex.memory.buffer, resPtr, resLen).slice();
            return decodeResult(blob);
        },
    };
}
/**
 * Instantiate munos-compiler.wasm from raw bytes and return it as a synchronous
 * MunosCompiler. Loading is async and happens once; `compile` is sync thereafter
 * — matching the existing call sites, which never had to await compilation.
 *
 * `bytes` is the wasm module. Node hosts get it from `fs.readFileSync` (see
 * `loadWasmCompiler`); the browser gets it from `fetch()` of the bundled asset.
 */
export async function instantiateWasmCompiler(bytes) {
    const { instance } = await WebAssembly.instantiate(bytes, {});
    return makeCompiler(instance);
}
/**
 * Synchronous instantiation of munos-compiler.wasm from raw bytes — for hosts
 * that already hold the bytes and want a compiler without an await (a Node
 * default backend built at module load, a browser boot that pre-fetched the
 * asset). `new WebAssembly.Module` is a synchronous compile; the same pattern
 * backs the game-wasm hosts (server/instance.ts, web/local-server.ts).
 */
export function instantiateWasmCompilerSync(bytes) {
    const module = new WebAssembly.Module(bytes);
    const instance = new WebAssembly.Instance(module, {});
    return makeCompiler(instance);
}
//# sourceMappingURL=wasm-compiler.js.map